import { memo, useEffect, useState } from "react";
import CodeEntry from "./CodeEntry";
import StringEntry from "./StringEntry";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import ReferenceEntry, { displayStringFromFHIRReference } from "./ReferenceEntry";
import UriEntry from "./UriEntry";
import PeriodEntry from "./PeriodEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const displayStringFromFHIRIdentifier = (identifier) => {
  if (identifier === undefined || identifier === null || identifier === '') {
    return ""
  }
  if (typeof identifier === "object") {
    let use = identifier.use || "";
    let type = displayStringFromFHIRCodeableConcept(identifier.type) || "";
    let system = identifier.system || "";
    let value = identifier.value || "[No identifier value.]";
    let assigner = displayStringFromFHIRReference(identifier.assigner) || "";
    if (system) {
      switch (system) {
        case 'https://clinicaltrials.gov':
          return `ClinicalTrials.gov ${value}`
        case 'https://doi.org':
          return `DOI ${value}`
        case 'https://www.crd.york.ac.uk/prospero/':
          return `PROSPERO ${value}`
        case 'https://fevir.net':
          if (type === "FOI" || type === "FEvIR Object Identifier") {
            return `FOI ${value}`
          } else if (type === "FLI" || type === "FEvIR Linking Identifier") {
            return `FEvIR Linking Identifier ${value}`
          } else {
            return `FEvIR Identifier ${value}`
          }
        case 'urn:ietf:rfc:3986':
          if (type === "FOI" || type === "FEvIR Object Identifier") {
            return `FOI ${value}`
          } else {
            return `Identifier ${value} from ${system}`
          }
        case 'https://fevir.net/FLI':
          return `FEvIR Linking Identifier ${value}`
        case 'https://www.ncbi.nlm.nih.gov/pmc/':
          return `PMCID ${value}`
        case 'https://www.ncbi.nlm.nih.gov/pmc':
          return `PMCID ${value}`
        case 'https://pubmed.ncbi.nlm.nih.gov':
          return `PMID ${value}`
        case 'https://pubmed.ncbi.nlm.nih.gov/':
          return `PMID ${value}`
        case 'https://orcid.org/':
          return `ORCID ${value}`
        case 'https://orcid.org':
          return `ORCID ${value}`
        default:
          return `Identifier ${value} from ${system}`;
      }
    }
    if (type) {
      let typeLower = type.toLowerCase();
      if (!typeLower.includes("identifier") || !typeLower.includes(" id")) {
        return `${type} identifier ${value}`;
      } else {
        return `${type} ${value}`;
      }
    }
    if (assigner) {
      return `identifier ${value} assigned by ${assigner}`
    }
    if (use) {
      return `${use} identifier ${value}`
    }
    return `${value}`
  }
  if (typeof identifier === 'string') {
    return `${identifier} [ERROR: Identifier data not in expected Identifier datatype.]`;
  }
  return "[ERROR: Identifier data not interpretable.]";
}

const identifierDotUseValues = ["usual", "official", "temp", "secondary", "old"];
const IdentifierEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState,
  startCollapsed, preserveFOI, inTableCell, dataEntryStyle }) => {

  let startingIdentifier = { "use": "", "type": "", "system": "", "value": "", "period": "", "assigner": "" };
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingIdentifier.extension = startingValue.extension; }
    if (startingValue.use) { startingIdentifier.use = startingValue.use; }
    if (startingValue.type) { startingIdentifier.type = startingValue.type; }
    if (startingValue.system) { startingIdentifier.system = startingValue.system; }
    if (startingValue.value) { startingIdentifier.value = startingValue.value; }
    if (startingValue.period) { startingIdentifier.period = startingValue.period; }
    if (startingValue.assigner) { startingIdentifier.assigner = startingValue.assigner; }
  }
  let isFOI = false;
  if (preserveFOI) {
    if (startingValue?.type?.text === 'FEvIR Object Identifier' || startingValue?.type?.text === 'FOI') {
      isFOI = true;
    }
  }


  const [identifierState, setIdentifierState] = useState(JSON.parse(JSON.stringify(startingIdentifier || {})));
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect((() => {
    if (Object.keys(identifierState).length) {
      let newIdentifier = {};
      if (identifierState.extension) { newIdentifier.extension = identifierState.extension; }
      if (identifierState.use) { newIdentifier.use = identifierState.use; }
      if (identifierState.type && Object.keys(identifierState.type).length) { newIdentifier.type = identifierState.type; }
      if (identifierState.system) { newIdentifier.system = identifierState.system; }
      if (identifierState.value) { newIdentifier.value = identifierState.value; }
      if (identifierState.period && Object.keys(identifierState.period).length) {
        newIdentifier.period = identifierState.period;
      }
      if (identifierState.assigner && Object.keys(identifierState.assigner).length) {
        newIdentifier.assigner = identifierState.assigner;
      }
      if (Object.keys(newIdentifier).length === 0) {
        newIdentifier = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newIdentifier } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [identifierState]);


  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRIdentifier(startingIdentifier)}
        &nbsp;&nbsp;
        {!isFOI && <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />}
      </p>
    </>
  } else {
    return <>
      {isFOI ?
        <><p>FOI {startingValue.value ? startingValue.value : '[Missing]'}</p></>
        :
        <>
          <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
          <div style={{ marginLeft: "24px" }}>
            <p>
              <b>Identifier Value: </b>
              <StringEntry elementName='value' fieldLabel='Identifier Value' startingValue={identifierState.value} setResourceState={setIdentifierState} />
            </p>
            <CodeEntry elementName='use' fieldLabel='Identifier Use' startingValue={identifierState.use} setResourceState={setIdentifierState}
              allowedValues={identifierDotUseValues} dataEntryStyle='dropdown' />
            <CodeableConceptEntry elementName='type' fieldLabel='Type of Identifier'
              startingValue={identifierState.type} setResourceState={setIdentifierState}
              dataEntryStyle={"simple"} inTableCell={inTableCell} startCollapsed={true} />
            <UriEntry elementName='system' fieldLabel='URI for Identification System'
              startingValue={identifierState.system ?
                identifierState.system : dataEntryStyle === "humanAuthor" ?
                  "https://orcid.org/" : ""}
              setResourceState={setIdentifierState} />
            <PeriodEntry elementName='period' fieldLabel='Period (when Identifier is effective)'
              inTableCell={inTableCell}
              startingValue={identifierState.period} setResourceState={setIdentifierState} startCollapsed={true} />
            <ReferenceEntry elementName='assigner' fieldLabel='Assigner of Identifier'
              startingValue={identifierState.assigner} setResourceState={setIdentifierState}
              referencedResourceTypes={['Organization']} startCollapsed={true} />
          </div>
        </>
      }
    </>
  }
})

export default IdentifierEntry;
export { displayStringFromFHIRIdentifier };